import { createContext, PropsWithChildren, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import { useIsMobile } from 'hooks/useIsMobile';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { getPathForView, getViewFromPath, MainPageView } from 'pages/MainPage/utils';
import { APP_ROUTES } from 'routes/routePaths';
import constants, { FeatureFlag } from 'utils/Constants';
import { isGuestModeEnabled } from 'utils/utils';

export interface NavigationContextType {
  selectedView: MainPageView;
  navigateToView: (view: MainPageView) => void;
  isSidebarOpen: boolean;
  sidebarToggle: () => void;
}

export const NavigationContext = createContext<NavigationContextType | null>(null);

const NavigationProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const isMessengerFeatureEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const [storedSidebarOpen, setStoredSidebarOpen] = useLocalStorage(constants.SIDEBAR_OPEN, false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const isSidebarOpen = isMobile ? mobileSidebarOpen : storedSidebarOpen;

  useLayoutEffect(() => {
    if (isMobile) setMobileSidebarOpen(false);
  }, [pathname, isMobile]);

  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient?.getUserId() ?? '');
  const isMessengerEnabled = isMessengerFeatureEnabled && !isGuestUser;

  const selectedView = getViewFromPath(pathname, isMessengerEnabled);

  const navigateToView = useCallback(
    (view: MainPageView) => {
      const path = getPathForView(view, isMessengerEnabled);
      if (!path) return;
      if (isMobile) setMobileSidebarOpen(false);
      navigate(path);
    },
    [isMessengerEnabled, isMobile, navigate]
  );

  const sidebarToggle = useCallback(() => {
    if (isMobile) return setMobileSidebarOpen((prev) => !prev);

    setStoredSidebarOpen((prev) => !prev);
  }, [isMobile, setStoredSidebarOpen]);

  const value = useMemo(
    () => ({ selectedView, navigateToView, isSidebarOpen, sidebarToggle }),
    [selectedView, navigateToView, isSidebarOpen, sidebarToggle]
  );

  if (isGuestUser && pathname.startsWith(APP_ROUTES.messenger)) {
    return <Navigate to={APP_ROUTES.authRoot} replace />;
  }

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export default NavigationProvider;
